import React, { Children, KeyboardEvent } from "react";
import classNames from "classnames";
import { ArrowRightIcon } from "@icons";
import { ChildrenProps } from "../../types";
import styles from "./SelectableItem.module.scss";

type IconSize = 50 | 60 | 75 | 100;

const iconSizeClassBySize: Record<IconSize, string> = {
    50: styles.icon50,
    60: styles.icon60,
    75: styles.icon75,
    100: styles.icon100,
};

interface SelectableItemProps extends ChildrenProps {
    selected?: boolean;
    disabled?: boolean;
    onSelect?: () => void;
    icon?: React.ReactNode;
    iconSize?: IconSize;
    title: React.ReactNode;
    description?: React.ReactNode;
    className?: string;
    multiple?: boolean;
    instantSelect?: boolean;
    footer?: React.ReactNode;
}

function getSelectableItemRole({
    multiple,
    isButtonLike,
}: {
    multiple: boolean;
    isButtonLike: boolean;
}): "button" | "checkbox" | "radio" {
    if (isButtonLike) {
        return "button";
    }
    if (multiple) {
        return "checkbox";
    }
    return "radio";
}

export const SelectableItem: React.FC<SelectableItemProps> = ({
    selected = false,
    disabled = false,
    onSelect,
    icon,
    iconSize = 50,
    title,
    description,
    multiple = false,
    className,
    instantSelect = false,
    footer,
    children,
}) => {
    const hasCustomAction = Children.count(children) > 0;
    const isButtonLike = instantSelect || hasCustomAction;
    const role = getSelectableItemRole({ multiple, isButtonLike });
    const showActionSlot = hasCustomAction || instantSelect;

    const handleActivate = () => {
        if (disabled) {
            return;
        }
        onSelect?.();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleActivate();
        }
    };

    return (
        <div
            className={classNames(styles.root, className)}
            role={role}
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            aria-checked={isButtonLike ? undefined : selected}
            aria-pressed={isButtonLike && selected ? true : undefined}
            data-selected={selected && !instantSelect ? true : undefined}
            data-disabled={disabled || undefined}
            data-multiple={multiple || undefined}
            data-instant-select={instantSelect || undefined}
            data-custom-action={hasCustomAction || undefined}
            onClick={handleActivate}
            onKeyDown={handleKeyDown}
        >
            {icon ? (
                <div
                    className={classNames(
                        styles.icon,
                        iconSizeClassBySize[iconSize],
                    )}
                    aria-hidden="true"
                >
                    {icon}
                </div>
            ) : null}

            <div className={styles.content}>
                <div className={styles.text}>
                    <div className={styles.title}>{title}</div>
                    {description ? (
                        <div className={styles.description}>{description}</div>
                    ) : null}
                </div>
            </div>

            {showActionSlot ? (
                <div className={styles.customAction} aria-hidden="true">
                    {hasCustomAction ? (
                        children
                    ) : (
                        <ArrowRightIcon
                            className={styles.instantSelectIcon}
                            aria-hidden="true"
                        />
                    )}
                </div>
            ) : null}

            {footer ? <div className={styles.footer}>{footer}</div> : null}
        </div>
    );
};
